import { Location } from 'react-router-dom';

import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getSaveItemAnalytics } from '@/redux/modules/analytics';
import { getDeployment } from '@/redux/modules/config';
import {
    getSavedItems,
    postSaveItem,
    postUnsaveItem,
    SavedItemQueryType,
} from '@/redux/modules/item/saved/savedItems.api';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getItemSummaryCatalogId,
    getItemSummaryIsAvailable,
    getItemSummaryItemId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchSavedItemCountsIfNeeded } from '@/redux/modules/savedItemCount';
import { ItemSummary } from '@/types/item/ItemSummary';
import { ButtonLocation, trackItemUnsavedAnalytics } from '@/utils/amplitude/analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

import { fetchItemFacetsIfNeeded, getItemFacets } from '../../itemFacets';

type SubmitSaveItemParams = {
    buttonLocation: ButtonLocation;
    canHaveEasyShippingTag: boolean;
    catalogId?: number;
    itemId: number;
    location: Location;
};

export type ToggleSaveItemResponse = {
    isAvailable: boolean;
    itemId: number;
};

export const submitSaveItem = createAsyncThunk<
    ToggleSaveItemResponse,
    SubmitSaveItemParams,
    AsyncThunkConfigWithRejectValue
>(
    'la/ui/saveItem/submitSaveItem',
    async (
        { buttonLocation, catalogId: providedCatalogId, itemId, location },
        { dispatch, getState, rejectWithValue }
    ) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        const storedItemId = getItemSummaryItemId(state, itemId);
        const storedCatalogId = getItemSummaryCatalogId(state, itemId);

        try {
            await postSaveItem({
                authToken,
                bidderId,
                catalogId: providedCatalogId ?? storedCatalogId,
                deployment,
                itemId,
            });

            if (!storedItemId) {
                await dispatch(
                    fetchItemSummaryIfNeeded({
                        identifier: 'save-item',
                        itemId,
                    })
                );
            }

            if (!getItemFacets(state, itemId).categories) {
                await dispatch(fetchItemFacetsIfNeeded([itemId]));
            }

            const newState = getState();

            await dispatch(fetchSavedItemCountsIfNeeded([itemId], true));

            // Use newState to ensure we get the most up-to-date facets data
            getSaveItemAnalytics(newState, itemId, location, buttonLocation);
            const isAvailable = getItemSummaryIsAvailable(newState, itemId);

            return { isAvailable, itemId };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const submitUnsaveItem = createAsyncThunk<ToggleSaveItemResponse, number, AsyncThunkConfigWithRejectValue>(
    'la/ui/saveItem/submitUnsaveItem',
    async (itemId, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        const storedItemId = getItemSummaryItemId(state, itemId);

        try {
            await postUnsaveItem({
                authToken,
                bidderId,
                deployment,
                itemId,
            });
            if (!storedItemId) {
                await dispatch(
                    fetchItemSummaryIfNeeded({
                        identifier: 'saved-item',
                        itemId,
                    })
                );
            }
            await dispatch(fetchSavedItemCountsIfNeeded([itemId], true));

            trackItemUnsavedAnalytics({
                pageName: getAmplitudePageNameFromPath(window.location.pathname),
                url: getFullUrl(deployment, window.location.pathname),
            });

            return {
                isAvailable: false,
                itemId,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export type FetchSavedItemsResponse = {
    items: ItemSummary[];
    tab: MyFavoritesItemStatusTab;
    totalRecords: number;
};

export const fetchSavedItems = createAsyncThunk<
    FetchSavedItemsResponse,
    SavedItemQueryType,
    AsyncThunkConfigWithRejectValue
>('la/ui/saveItem/fetchSavedItems', async (query, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const bidderId = getBidderId(state);

    try {
        const { payload } = await getSavedItems({
            authToken,
            bidderId,
            deployment,
            query,
        });
        return {
            items: payload.items,
            tab: query.status,
            totalRecords: payload.totalRecords,
        };
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const toggleItemSave = createAsyncThunk<void, SubmitSaveItemParams>(
    'la/ui/saveItem/toggleItemSave',
    async ({ itemId, ...rest }, { dispatch, getState }) => {
        if (isItemSaved(getState(), itemId)) {
            await dispatch(submitUnsaveItem(itemId));
        } else {
            await dispatch(
                submitSaveItem({
                    itemId,
                    ...rest,
                })
            );
        }
    }
);
